/**
 * @fileoverview Theme name translations for all supported locales.
 * Keyed by the stable theme ID (e.g. 'animals', 'body_parts').
 */

import type { SupportedLang } from './lang.js';
import { currentLang } from './lang.js';

const themeTranslations: Record<SupportedLang, Record<string, string>> = {
    en: {
        activities:  'Activities',
        animals:     'Animals',
        birds:       'Birds',
        body_parts:  'Body Parts',
        books:       'Books',
        buildings:   'Buildings',
        clothing:    'Clothing',
        colors:      'Colors',
        faces:       'Faces',
        flags:       'Flags',
        food:        'Food',
        fruits:      'Fruits',
        household:   'Household',
        insects:     'Insects',
        letters:     'Letters',
        mammals:     'Mammals',
        music:       'Music',
        nature:      'Nature',
        numbers:     'Numbers',
        ocean:       'Ocean Creatures',
        puzzle:      'Puzzle',
        reptiles:    'Reptiles',
        school:      'School',
        sea:         'Sea Creatures',
        shapes:      'Shapes',
        space:       'Space',
        sports:      'Sports',
        symbols:     'Symbols',
        tech:        'Technology',
        time:        'Time',
        tools:       'Tools',
        vehicles:    'Vehicles',
        weather:     'Weather',
    },
    de: {
        activities:  'Aktivitäten',
        animals:     'Tiere',
        birds:       'Vögel',
        body_parts:  'Körperteile',
        books:       'Bücher',
        buildings:   'Gebäude',
        clothing:    'Kleidung',
        colors:      'Farben',
        faces:       'Gesichter',
        flags:       'Flaggen',
        food:        'Essen',
        fruits:      'Früchte',
        household:   'Haushalt',
        insects:     'Insekten',
        letters:     'Buchstaben',
        mammals:     'Säugetiere',
        music:       'Musik',
        nature:      'Natur',
        numbers:     'Zahlen',
        ocean:       'Meeresbewohner',
        puzzle:      'Rätsel',
        reptiles:    'Reptilien',
        school:      'Schule',
        sea:         'Meerestiere',
        shapes:      'Formen',
        space:       'Weltraum',
        sports:      'Sport',
        symbols:     'Symbole',
        tech:        'Technologie',
        time:        'Zeit',
        tools:       'Werkzeuge',
        vehicles:    'Fahrzeuge',
        weather:     'Wetter',
    },
    es: {
        activities:  'Actividades',
        animals:     'Animales',
        birds:       'Aves',
        body_parts:  'Partes del cuerpo',
        books:       'Libros',
        buildings:   'Edificios',
        clothing:    'Ropa',
        colors:      'Colores',
        faces:       'Caras',
        flags:       'Banderas',
        food:        'Comida',
        fruits:      'Frutas',
        household:   'Hogar',
        insects:     'Insectos',
        letters:     'Letras',
        mammals:     'Mamíferos',
        music:       'Música',
        nature:      'Naturaleza',
        numbers:     'Números',
        ocean:       'Criaturas del océano',
        puzzle:      'Rompecabezas',
        reptiles:    'Reptiles',
        school:      'Escuela',
        sea:         'Criaturas del mar',
        shapes:      'Formas',
        space:       'Espacio',
        sports:      'Deportes',
        symbols:     'Símbolos',
        tech:        'Tecnología',
        time:        'Tiempo',
        tools:       'Herramientas',
        vehicles:    'Vehículos',
        weather:     'Clima',
    },
    fr: {
        activities:  'Activités',
        animals:     'Animaux',
        birds:       'Oiseaux',
        body_parts:  'Parties du corps',
        books:       'Livres',
        buildings:   'Bâtiments',
        clothing:    'Vêtements',
        colors:      'Couleurs',
        faces:       'Visages',
        flags:       'Drapeaux',
        food:        'Nourriture',
        fruits:      'Fruits',
        household:   'Maison',
        insects:     'Insectes',
        letters:     'Lettres',
        mammals:     'Mammifères',
        music:       'Musique',
        nature:      'Nature',
        numbers:     'Nombres',
        ocean:       "Créatures de l'océan",
        puzzle:      'Puzzle',
        reptiles:    'Reptiles',
        school:      'École',
        sea:         'Créatures de la mer',
        shapes:      'Formes',
        space:       'Espace',
        sports:      'Sports',
        symbols:     'Symboles',
        tech:        'Technologie',
        time:        'Temps',
        tools:       'Outils',
        vehicles:    'Véhicules',
        weather:     'Météo',
    },
    pt: {
        activities:  'Atividades',
        animals:     'Animais',
        birds:       'Pássaros',
        body_parts:  'Partes do corpo',
        books:       'Livros',
        buildings:   'Edifícios',
        clothing:    'Roupas',
        colors:      'Cores',
        faces:       'Rostos',
        flags:       'Bandeiras',
        food:        'Comida',
        fruits:      'Frutas',
        household:   'Casa',
        insects:     'Insetos',
        letters:     'Letras',
        mammals:     'Mamíferos',
        music:       'Música',
        nature:      'Natureza',
        numbers:     'Números',
        ocean:       'Criaturas do oceano',
        puzzle:      'Quebra-cabeça',
        reptiles:    'Répteis',
        school:      'Escola',
        sea:         'Criaturas do mar',
        shapes:      'Formas',
        space:       'Espaço',
        sports:      'Esportes',
        symbols:     'Símbolos',
        tech:        'Tecnologia',
        time:        'Tempo',
        tools:       'Ferramentas',
        vehicles:    'Veículos',
        weather:     'Clima',
    },
    bs: {
        activities:  'Aktivnosti',
        animals:     'Životinje',
        birds:       'Ptice',
        body_parts:  'Dijelovi tijela',
        books:       'Knjige',
        buildings:   'Zgrade',
        clothing:    'Odjeća',
        colors:      'Boje',
        faces:       'Lica',
        flags:       'Zastave',
        food:        'Hrana',
        fruits:      'Voće',
        household:   'Domaćinstvo',
        insects:     'Insekti',
        letters:     'Slova',
        mammals:     'Sisari',
        music:       'Muzika',
        nature:      'Priroda',
        numbers:     'Brojevi',
        ocean:       'Okeanska stvorenja',
        puzzle:      'Slagalica',
        reptiles:    'Reptili',
        school:      'Škola',
        sea:         'Morska stvorenja',
        shapes:      'Oblici',
        space:       'Svemir',
        sports:      'Sportovi',
        symbols:     'Simboli',
        tech:        'Tehnologija',
        time:        'Vrijeme',
        tools:       'Alati',
        vehicles:    'Vozila',
        weather:     'Vrijeme',
    },
};

/** Resolved theme name map for the current locale. Falls back to English name if key missing. */
export const themeNames: Record<string, string> = themeTranslations[currentLang];
